import { useEffect, useState } from 'react';
import { Badge, Button, Card, cx, Drawer } from '../Ui';
import { formatLocalMinor, formatUsd } from '../../lib/currency';
import {
  deliveryTypeLabels,
  fulfillmentLabels,
  getCriticality,
  orderStatusLabels,
  orderStatusTone,
} from './control-tower-utils';
import type { OrderItemView, OrderView } from './control-tower-utils';

interface ControlTowerOrderDrawerProps {
  open: boolean;
  order: OrderView | null;
  onOpenChange(open: boolean): void;
}

function useDesktopDrawer(): boolean {
  const [desktop, setDesktop] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  ));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(min-width: 768px)');
    const update = () => setDesktop(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return desktop;
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('es', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(ms: number | null): string {
  if (ms == null) return 'Sin datos';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return 'Menos de 1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining ? `${hours} h ${remaining} min` : `${hours} h`;
}

function OptionSummary({ options }: { options: OrderItemView['options'] }) {
  if (!options.length) return null;
  return <p className="text-xs text-slate-600"><strong>Opciones:</strong> {options.map((option) => `${option.groupName}: ${option.quantity > 1 ? `${option.quantity}× ` : ''}${option.optionName}`).join(' · ')}</p>;
}

function ItemDetail({ item }: { item: OrderItemView }) {
  const includedIngredients = item.ingredients.filter((ingredient) => ingredient.included && !ingredient.excluded);
  const excludedIngredients = item.ingredients.filter((ingredient) => ingredient.excluded);
  return (
    <Card className="grid gap-3 p-4 shadow-none">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-black text-slate-950">{item.quantity}× {item.productName}</h4>
            {item.variantName && <Badge tone="purple">{item.variantName}</Badge>}
            {item.productType === 'combo' && <Badge tone="purple">Combo</Badge>}
          </div>
          <p className="text-xs text-slate-500">Zona {item.preparationZone}</p>
        </div>
        <div className="text-right text-sm">
          <div className="font-black">{formatUsd(item.unitPriceUsdCents)}</div>
          <div className="text-slate-500">Subtotal {formatUsd(item.subtotalUsdCents)}</div>
        </div>
      </div>
      <OptionSummary options={item.options} />
      {includedIngredients.length > 0 && <p className="text-xs text-slate-600"><strong>Ingredientes:</strong> {includedIngredients.map((ingredient) => ingredient.name).join(', ')}</p>}
      {excludedIngredients.length > 0 && <p className="text-xs font-semibold text-rose-700"><strong>Sin:</strong> {excludedIngredients.map((ingredient) => ingredient.name).join(', ')}</p>}
      {item.components.length > 0 && (
        <div className="grid gap-2 border-l-2 border-violet-200 pl-3">
          {item.components.map((component) => (
            <div key={component.id} className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-black text-violet-800">{component.componentName}</span>
                <span className="font-semibold">{component.quantity}× {component.productName}</span>
                {component.variantName && <Badge tone="purple">{component.variantName}</Badge>}
                <span className="text-slate-500">Zona {component.preparationZone}</span>
              </div>
              {component.options.length > 0 && <p className="mt-2 text-xs text-slate-600"><strong>Opciones:</strong> {component.options.map((option) => `${option.groupName}: ${option.quantity > 1 ? `${option.quantity}× ` : ''}${option.optionName}`).join(' · ')}</p>}
              {component.ingredients.filter((ingredient) => ingredient.included && !ingredient.excluded).length > 0 && <p className="mt-2 text-xs text-slate-600"><strong>Ingredientes:</strong> {component.ingredients.filter((ingredient) => ingredient.included && !ingredient.excluded).map((ingredient) => ingredient.name).join(', ')}</p>}
              {component.excludedIngredients.length > 0 && <p className="mt-2 text-xs font-semibold text-rose-700"><strong>Sin:</strong> {component.excludedIngredients.map((ingredient) => ingredient.name).join(', ')}</p>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function ControlTowerOrderDrawer({ open, order, onOpenChange }: ControlTowerOrderDrawerProps) {
  const desktop = useDesktopDrawer();
  if (!order) return null;
  return (
    <Drawer.Backdrop isOpen={open} onOpenChange={onOpenChange} variant="blur">
      <Drawer.Content placement={desktop ? 'right' : 'bottom'}>
        <Drawer.Dialog aria-label={`Detalle del pedido #${String(order.correlation).padStart(2, '0')}`} className={cx(desktop ? 'h-full w-[min(96vw,760px)]' : 'h-full w-full', 'border-l border-border bg-surface')}>
          {!desktop && <Drawer.Handle />}
          <Drawer.CloseTrigger aria-label="Cerrar detalle del pedido" />
          <ControlTowerOrderDrawerContent order={order} />
          <Drawer.Footer className="border-t border-border px-4 py-4 sm:px-6">
            <Button slot="close" variant="secondary">Cerrar</Button>
          </Drawer.Footer>
        </Drawer.Dialog>
      </Drawer.Content>
    </Drawer.Backdrop>
  );
}

export function ControlTowerOrderDrawerContent({ order }: { order: OrderView }) {
  const criticality = getCriticality(order, Date.now());
  return (
    <>
      <Drawer.Header className="border-b border-border px-5 py-4">
        <div className="grid gap-2">
          <Drawer.Heading className="text-xl font-black text-foreground">Pedido #{String(order.correlation).padStart(2, '0')}</Drawer.Heading>
          <div className="flex flex-wrap gap-2">
            <Badge tone={orderStatusTone[order.status]}>{orderStatusLabels[order.status]}</Badge>
            {order.fulfillmentStage && <Badge tone="blue">{fulfillmentLabels[order.fulfillmentStage]}</Badge>}
            <Badge tone={criticality.level === 'critical' ? 'red' : criticality.level === 'warning' ? 'amber' : 'green'}>{criticality.label}</Badge>
          </div>
          <p className="text-sm text-slate-500">Creado {formatDateTime(order.createdAt)}</p>
        </div>
      </Drawer.Header>
      <Drawer.Body className="grid gap-4 overflow-y-auto px-4 py-5 sm:px-6">
        <Card className="grid gap-2">
          <h3 className="font-black text-slate-950">Cliente y entrega</h3>
          <p><strong>Cliente:</strong> {order.customerName || 'Cliente'}</p>
          <p><strong>Teléfono:</strong> {order.customerPhone || 'Sin teléfono'}</p>
          <p><strong>Tipo:</strong> {deliveryTypeLabels[order.deliveryType]}</p>
          {order.deliveryAddress && <p><strong>Dirección:</strong> {order.deliveryAddress}</p>}
          {order.notes && <p><strong>Notas:</strong> {order.notes}</p>}
          {order.confirmedMapsUrl && <p className="break-all"><strong>Ubicación confirmada:</strong> <a className="font-semibold text-sky-700 underline" href={order.confirmedMapsUrl} target="_blank" rel="noreferrer">{order.confirmedMapsUrl}</a></p>}
        </Card>

        <section className="grid gap-3">
          <h3 className="font-black text-slate-950">Detalle comercial</h3>
          {order.items.map((item) => <ItemDetail key={item.id} item={item} />)}
        </section>

        <Card className="grid gap-2">
          <h3 className="font-black text-slate-950">Totales</h3>
          <p><strong>Total USD:</strong> {formatUsd(order.totalUsdCents)}</p>
          <p><strong>Total local:</strong> {formatLocalMinor(order.totalLocalMinor, order.localCurrency, order.currencyDecimals, order.currencySymbol)}</p>
          <p><strong>Tasa de cambio aplicada:</strong> {order.exchangeRateApplied}</p>
          <p><strong>Moneda:</strong> {order.localCurrency} · {order.currencySymbol} · {order.currencyDecimals} decimales</p>
        </Card>

        <Card className="grid gap-2">
          <h3 className="font-black text-slate-950">Operación</h3>
          <p><strong>Recepción:</strong> {formatDuration(order.fulfillmentTimings.receptionMs)}</p>
          <p><strong>Cocina:</strong> {formatDuration(order.fulfillmentTimings.kitchenMs)}</p>
          <p><strong>Espera:</strong> {formatDuration(order.fulfillmentTimings.readyWaitMs)}</p>
          <p><strong>Tránsito:</strong> {formatDuration(order.fulfillmentTimings.transitMs)}</p>
          <p><strong>Modificado por recepción:</strong> {order.modifiedByReception ? 'Sí' : 'No'}</p>
          <p><strong>Actualizado:</strong> {formatDateTime(order.updatedAt)}</p>
        </Card>
      </Drawer.Body>
    </>
  );
}
